import React, { useContext, useEffect, useState } from 'react';
import GroupMindsetResultImg from './GroupMindsetResultImg';
import HeaderPreview from "../../../GeneratePdf/HeaderPreview";
import FooterPreview from "../../../GeneratePdf/FooterPreview";
import GroupReportContext from '../../../../Context/GroupReportContext';
import { useGroupReportDate } from '../../../../Context/GroupReportDate';
import { getMindsetSum } from '../../../../utils/helper';

export default function GroupMindsetResult({ apiData, previewData, reportType, pageNumber }) {
  const [calculatedPercentage, setCalculatedPercentage] = useState(0);
  const { getPercentileFromScore } = useContext(GroupReportContext);
  const { averagePercentagesMindset: contextAverage } = useContext(GroupReportContext);
  const { updateMindsetscore } = useGroupReportDate();

  const pickResults = (previewData, apiData, reportType) => {
    if (Array.isArray(previewData)) {
      const match = previewData.find(x => x?.reportType === reportType);
      if (match?.results) return match.results;
    }
    if (Array.isArray(apiData?.results)) return apiData.results;
    return [];
  };

  useEffect(() => {
    const dataToProcess = pickResults(previewData, apiData, reportType);

    if (!Array.isArray(dataToProcess) || dataToProcess.length === 0) {
      setCalculatedPercentage(contextAverage);
      return;
    }

    const memberNormalizedScores = [];

    dataToProcess.forEach((member) => {
      const variables = Array.isArray(member?.variables) ? member.variables : [];
      if (variables.length === 0) {
        return;
      }

      const mindsetSum = getMindsetSum(variables);
      if (mindsetSum > 0) {
        // Normalize each user's mindset first, then average
        memberNormalizedScores.push(getPercentileFromScore(mindsetSum));
      }
    });

    if (memberNormalizedScores.length > 0) {
      const avgNormalized = memberNormalizedScores.reduce((a, b) => a + b, 0) / memberNormalizedScores.length;
      setCalculatedPercentage(avgNormalized);
    } else {
      setCalculatedPercentage(contextAverage);
    }
  }, [previewData, apiData, reportType, contextAverage, getPercentileFromScore]);

  // calculatedPercentage already represents normalized score (percentile)
  const percentage = (typeof calculatedPercentage === 'number' ? calculatedPercentage : 0) || (contextAverage || 0);
  const roundedPercentage = Number((percentage || 0).toFixed(1));

  // console.log('Rendering with percentage:', percentage);

  // Save the percentage to mindsetscore context
  useEffect(() => {
    updateMindsetscore(roundedPercentage);
  }, [roundedPercentage, updateMindsetscore]);

  if ((!previewData || !previewData[0]?.results) && !apiData?.data) {
    return <div>No data available</div>;
  }

  const mindsetContent = {
    growth: {
      title: 'Group Results: Adaptive & Transformative',
      bullets: [
        // Adaptive Learner
        { label: 'Belief', text: '“My abilities can grow with effort and strategies.”' },
        { label: 'Focus', text: 'Seeks diverse challenges for development, even at the risk of failure.' },
        { label: 'Response to Feedback', text: 'Welcomes feedback as a tool for improvement.' },
        { label: 'View of Effort', text: 'Effort is necessary and rewarding for growth.' },
        { label: 'Reaction to Failure', text: 'Views failure as an opportunity to learn and adapt.' },
        // Transformative Growth
        { label: 'Belief', text: '“My potential is unlimited with persistence and learning.”' },
        { label: 'Focus', text: 'Actively pursues growth in all areas, even outside comfort zones.' },
        { label: 'Response to Feedback', text: 'Actively seeks constructive criticism to refine skills.' },
        { label: 'View of Effort', text: 'Embraces effort as the core driver of improvement.' },
        { label: 'Reaction to Failure', text: 'Thrives on failure as essential for transformation.' },
        
      ],
      paragraphs: [
        'The group results indicate that you collectively embody a <b>growth mindset,</b> aligning with the Adaptive Learner and Transformative Growth levels. This mindset reflects your shared belief that abilities and intelligence can be developed through effort, learning, and persistence. It’s a powerful perspective that turns challenges into opportunities and views setbacks as essential steps toward mastery.',
        'With a growth mindset, your group actively embraces challenges and seeks out opportunities to improve, welcoming feedback as a valuable tool for growth. You collectively recognize effort as a pathway to progress and are not discouraged by setbacks, using them as lessons to refine your approach and build resilience.',
        'This mindset also positively impacts those around you. By valuing collaboration and viewing others’ successes as sources of inspiration rather than competition, your group fosters supportive and innovative environments. Your shared ability to integrate learning and persistence contributes to stronger relationships, teamwork, and leadership.',
        'Your group’s commitment to continuous improvement sets the foundation for personal and professional success. By maintaining this perspective, your team is well-equipped to navigate challenges, achieve meaningful growth, and inspire others to do the same. Keep leveraging this mindset to unlock even greater potential in yourselves and those you influence.'
      ]
    },
    conditional: {
      title: 'Group Results: Conditional Growth',
      bullets: [
        // Conditional Growth
        { label: 'Belief', text: '“I can grow, but only in areas where I’m naturally talented.”' },
        { label: 'Focus', text: 'Selectively takes on challenges aligned with perceived strengths.' },
        { label: 'Response to Feedback', text: 'Accepts feedback when it aligns with existing beliefs.' },
        { label: 'View of Effort', text: 'Effort is valuable but tied to success rather than growth.' },
        { label: 'Reaction to Failure', text: 'Sees failure as situational, not personal, but avoids risk.' },
      ],
      paragraphs: [
        'Your group results show that you collectively align with a <b>mixed mindset,</b> a mindset that combines elements of both fixed and growth perspectives. This means your group believes growth is possible but often within areas where there is a shared sense of natural talent or confidence. You value effort, though it’s often tied to achieving success rather than the process of growth itself.',
        'At this stage, your group may prefer challenges that match your strengths and feel comfortable with feedback that reinforces what you already know. While failure doesn’t feel personal, your team might hesitate to take risks that lead to uncertain outcomes.',
        'To grow further, try viewing effort as a key part of learning, not just a path to success. Take small steps to embrace challenges outside your comfort zone and seek feedback that helps the team stretch and improve. Your current mindset provides a strong starting point—by focusing on progress over perfection, you can build resilience and unlock greater opportunities for collective growth and success.',
        'By shifting your perspective and celebrating growth as a shared journey, your group can start to explore new possibilities beyond your natural strengths. This mindset shift will not only enhance group development but also positively impact your relationships, leadership, and collaboration with others. Growth isn’t just about ability—it’s about discovering your collective potential through persistence, adaptability, and a willingness to take on new challenges.'
      ]
    },
    fixed: {
      title: 'Group Results: Rigid & Cautious',
      bullets: [
        // Rigid Self-Definition
        { label: 'Belief', text: '“My abilities and intelligence are unchangeable.”' },
        { label: 'Focus', text: 'Avoiding challenges to protect self-image.' },
        { label: 'Response to Feedback', text: 'Defensiveness or rejection of constructive criticism.' },
        { label: 'View of Effort', text: 'Sees effort as a sign of weakness.' },
        { label: 'Reaction to Failure', text: 'Internalizes failure as personal inadequacy.' },
        // Cautious Openness
        { label: 'Belief', text: '“I might improve, but only within certain limits.”' },
        { label: 'Focus', text: 'Prefers familiar challenges with predictable outcomes.' },
        { label: 'Response to Feedback', text: 'Willing to listen but may struggle to act.' },
        { label: 'View of Effort', text: 'Views effort as useful but only in specific situations.' },
        { label: 'Reaction to Failure', text: 'Avoids discussing failure but acknowledges it internally.' },
      ],
      paragraphs: [
        'The group results indicate that your team may currently align with aspects of a <b>fixed mindset,</b> particularly within the Rigid Self-Definition and Cautious Openness levels. This mindset suggests a collective belief that abilities and intelligence are relatively static, which can sometimes limit risk-taking and openness to challenges. Recognizing this mindset is an important step toward exploring new possibilities and fostering growth.',
        'With a fixed mindset, your group might prioritize avoiding challenges to maintain comfort or protect how your group is perceived. Setbacks may feel discouraging, but they also present opportunities for reflection and growth. Shifting your collective perspective to see difficult tasks as chances to develop new skills can help reframe failure as part of the learning process rather than a limitation.',
        'Taking small, intentional steps can create momentum for change. Gradually embracing challenges, seeking feedback, and celebrating progress—no matter how small—can help your group move beyond the limitations of a fixed mindset. These efforts encourage a more flexible and adaptive approach to personal and professional growth.',
        'This shift not only benefits your group’s development but also strengthens your relationships and team interactions. By viewing effort as valuable and feedback as a tool for improvement, your group can begin to foster resilience and confidence in both yourselves and others.',
        'Remember, growth is a journey, not a destination. By making intentional changes together, your group can build a mindset that unlocks potential, strengthens connections, and achieves meaningful progress in all areas of life.'
      ]
    }
  };

  // Using the already calculated percentage from above
  // which is: const percentage = calculatedPercentage || Math.round(contextAverage);

  // NEW MINDSET SCORING
  // 14 % to 27 % Low: 12 - 23(11 points) FIXED
  // 28 % to 44 % Mid - Low: 24 - 37(14 points) FIXED
  // 45 % to 70 % Mid: 38 - 59(22 points) CONDITIONAL
  // 71 % to 87 % Mid - High: 60 - 73(14 points) GROWTH
  // 88 % to 100 % High: 74 - 84(11 points) GROWTH

  // source - https://studio137workspace.slack.com/archives/D083DPRGXPV/p1753879174594789?thread_ts=1753878422.784669&cid=D083DPRGXPV

  const getSelectedContentByPercentage = (percentage) => {
    if (percentage >= 0 && percentage < 16) {
      return mindsetContent.fixed;
    } else if (percentage >= 16 && percentage < 36) {
      return mindsetContent.fixed;
    } else if (percentage >= 36 && percentage < 66) {
      return mindsetContent.conditional;
    } else if (percentage >= 66 && percentage < 86) {
      return mindsetContent.growth;
    } else if (percentage >= 86 && percentage <= 100) {
      return mindsetContent.growth;
    } else {
      return mindsetContent.growth;
    }
  };

  const selectedContent = getSelectedContentByPercentage(Math.round(percentage));

  const renderGrowthBullets = (percentage) => {
    if (percentage >= 86) {
      return (
        <ul style={{ listStyle: 'disc', paddingLeft: '9px', margin: 0, lineHeight: '1.5' }}>
          {selectedContent.bullets.slice(5).map((item, idx) => (
            <li key={idx} style={{
              fontFamily: 'Helvetica, sans-serif',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: '10px',
              lineHeight: '14px',
              letterSpacing: '0px',
              color: '#000000',
              marginBottom: idx === selectedContent.bullets.length - 6 ? '0px' : '2px'
            }}>
              <span style={{ fontWeight: 700 }}>{item.label}:</span> {item.text}
            </li>
          ))}
        </ul>
      );
    } else {
      return (
        <ul style={{ listStyle: 'disc', paddingLeft: '9px', margin: 0, lineHeight: '1.5' }}>
          {selectedContent.bullets.slice(0, 5).map((item, idx) => (
            <li key={idx} style={{
              fontFamily: 'Helvetica, sans-serif',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: '10px',
              lineHeight: '14px',
              letterSpacing: '0px',
              color: '#000000',
              marginBottom: '2px'
            }}>
              <span style={{ fontWeight: 700 }}>{item.label}:</span> {item.text}
            </li>
          ))}
        </ul>
      );
    }
  };

  const renderFixedBullets = (percentage) => {
    if (percentage < 16) {
      return (
        <ul style={{ listStyle: 'disc', paddingLeft: '9px', margin: 0, lineHeight: '1.5' }}>
          {selectedContent.bullets.slice(0, 5).map((item, idx) => (
            <li key={idx} style={{
              fontFamily: 'Helvetica, sans-serif',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: '10px',
              lineHeight: '14px',
              letterSpacing: '0px',
              color: '#000000',
              marginBottom: '2px'
            }}>
              <span style={{ fontWeight: 700 }}>{item.label}:</span> {item.text}
            </li>
          ))}
        </ul>
      );
    } else {
      return (
        <ul style={{ listStyle: 'disc', paddingLeft: '9px', margin: 0, lineHeight: '1.5' }}>
          {selectedContent.bullets.slice(5).map((item, idx) => (
            <li key={idx} style={{
              fontFamily: 'Helvetica, sans-serif',
              fontWeight: 400,
              fontStyle: 'normal',
              fontSize: '10px',
              lineHeight: '14px',
              letterSpacing: '0px',
              color: '#000000',
              marginBottom: idx === selectedContent.bullets.length - 6 ? '0px' : '2px'
            }}>
              <span style={{ fontWeight: 700 }}>{item.label}:</span> {item.text}
            </li>
          ))}
        </ul>
      );
    }
  };

  const getArrowPosition = (percentage) => {
    const numPercentage = Math.round(Number(percentage));

    // Match the same ranges as getMindsetData function
    if (numPercentage >= 0 && numPercentage < 16) {
      return "20%"; // Rigid Self-Definition (Dark Blue)
    } else if (numPercentage >= 16 && numPercentage < 36) {
      return "35.5%"; // Cautious Openness (Light Blue)
    } else if (numPercentage >= 36 && numPercentage < 66) {
      return "51.5%"; // Conditional Growth (Yellow)
    } else if (numPercentage >= 66 && numPercentage < 86) {
      return "66.5%"; // Adaptive Learner (Light Green)
    } else if (numPercentage >= 86 && numPercentage <= 100) {
      return "82%"; // Transformative Growth (Dark Green)
    } else if (numPercentage > 100) {
      return "82%"; // Above maximum, use Transformative
    } else if (numPercentage < 0) {
      return "19%"; // Below minimum, use Rigid
    }

    return "50%"; // Default fallback
  };

  return (
    <div>
      <HeaderPreview />
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}>
        <h1 style={{ fontFamily: 'Helvetica, sans-serif', fontWeight: 700, fontStyle: 'normal', fontSize: '14px', lineHeight: '18px', letterSpacing: '0px', textAlign: 'center', textTransform: 'uppercase', color: '#00577D', padding: "16px 0px", margin: "0px" }}>
          YOUR MINDSET RESULTS
        </h1>

        <GroupMindsetResultImg score={roundedPercentage} />

        <div style={{
          position: "relative",
          background: "#EAF1F5",
          width: "468px",
          padding: "5px 10px",
          borderRadius: "5px",
          boxSizing: "border-box",
          marginTop: "10px"
        }}>
          <div style={{ position: 'absolute', top: '-19px', left: 0, right: 0, height: '19px', overflow: 'visible', zIndex: 2 }}>
            <div style={{ width: 0, height: 0, borderLeft: "6px solid transparent", borderRight: "6px solid transparent", borderBottom: "14px solid #EAF1F5", position: "absolute", left: getArrowPosition(percentage), marginLeft: '-10px', top: "6px" }} />
          </div>
          {selectedContent === mindsetContent.growth
            ? renderGrowthBullets(Math.round(percentage))
            : selectedContent === mindsetContent.fixed
              ? renderFixedBullets(Math.round(percentage))
              : (
                <ul style={{ listStyle: 'disc', paddingLeft: '9px', margin: 0, lineHeight: '1.5' }}>
                  {selectedContent.bullets.map((item, idx) => (
                    <li key={idx} style={{
                      fontFamily: 'Helvetica, sans-serif',
                      fontWeight: 400,
                      fontStyle: 'normal',
                      fontSize: '10px',
                      lineHeight: '14px',
                      letterSpacing: '0px',
                      color: '#000000',
                    }}>
                      <span style={{ fontWeight: 700 }}>{item.label}:</span> {item.text}
                    </li>
                  ))}
                </ul>
              )}
          <div style={{
            fontFamily: 'Helvetica, sans-serif',
            fontWeight: 400,
            fontStyle: 'oblique',
            fontSize: '10px',
            lineHeight: '14px',
            letterSpacing: '0px',
            color: '#00577D',
            marginTop: '4px',
            textAlign: 'right'
          }}>
            Credit: Healthy Growing Leaders
          </div>
        </div>

        <div style={{ marginTop: "16px" }}>
          <div style={{ marginBottom: '16px' }}>
            <p style={{
              fontFamily: 'Helvetica, sans-serif',
              fontWeight: 700,
              fontStyle: 'normal',
              fontSize: '10px',
              lineHeight: '14px',
              letterSpacing: '0px',
              color: '#000000',
              margin: "0px 0 8px 0"
            }}>
              {selectedContent.title}
            </p>
            {selectedContent.paragraphs.map((p, idx) => (
              <p
                key={idx}
                style={{
                  fontFamily: 'Helvetica, sans-serif',
                  fontWeight: 400,
                  fontSize: '10px',
                  lineHeight: '14px',
                  color: '#000000',
                  margin: '0 0 8px 0'
                }}
                dangerouslySetInnerHTML={{ __html: p }}
              />
            ))}
          </div>
        </div>
      </div>
      <FooterPreview pageNumber={pageNumber} reportType={reportType} />
    </div>
  );
}
